import React, { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { TailSpin } from 'components/tail-spin';

export enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
}

export enum ButtonFontSize {
    Small = 'small',
    Medium = 'medium',
    Base = 'base',
}

export enum ButtonBackgroundColor {
    Gray = 'gray',
    Default = 'default',
}

type ButtonStyleProps = {
    variant: ButtonVariant;
    isLoading?: boolean;
    fullWidth?: boolean;
    isPrependIcon?: boolean;
    isAppendIcon?: boolean;
    compact?: boolean;
    fontSize?: ButtonFontSize;
    backgroundColor?: ButtonBackgroundColor;
};

const getBackgroundColor = (
    backgroundColor: ButtonBackgroundColor | undefined,
    defaultColor: string,
    grayColor: string,
) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return grayColor;
        case ButtonBackgroundColor.Default:
        default:
            return defaultColor;
    }
};

const getHoverBackgroundColor = (
    backgroundColor: ButtonBackgroundColor | undefined,
    defaultColor: string,
    grayColor: string,
) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return grayColor;
        case ButtonBackgroundColor.Default:
        default:
            return defaultColor;
    }
};

const getTextColor = (
    backgroundColor: ButtonBackgroundColor | undefined,
    defaultColor: string,
    grayColor: string,
) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return grayColor;
        case ButtonBackgroundColor.Default:
        default:
            return defaultColor;
    }
};

const getBoxShadow = (backgroundColor: ButtonBackgroundColor | undefined) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return 'none';
        case ButtonBackgroundColor.Default:
        default:
            return '0 8px 18px rgba(77, 61, 9, 0.12)';
    }
};

const getBorder = (backgroundColor: ButtonBackgroundColor | undefined) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return 'none';
        case ButtonBackgroundColor.Default:
        default:
            return '2px solid';
    }
};

const getBorderColor = (
    backgroundColor: ButtonBackgroundColor | undefined,
    defaultColor: string,
) => {
    switch (backgroundColor) {
        case ButtonBackgroundColor.Gray:
            return 'transparent';
        case ButtonBackgroundColor.Default:
        default:
            return defaultColor;
    }
};

const getFontSize = (fontSize: ButtonFontSize | undefined, compact: boolean, theme: any) => {
    const effectiveFontSize = compact && !fontSize ? ButtonFontSize.Small : fontSize;

    switch (effectiveFontSize) {
        case ButtonFontSize.Small:
            return theme.font.size.small;
        case ButtonFontSize.Medium:
            return theme.font.size.medium;
        case ButtonFontSize.Base:
        default:
            return theme.font.size.base;
    }
};

const getPadding = (compact: boolean, isPrependIcon: boolean, isAppendIcon: boolean): string => {
    if (compact) {
        if (isPrependIcon && isAppendIcon) return '6px 36px';
        if (isPrependIcon) return '6px 6px 6px 36px';
        if (isAppendIcon) return '6px 36px 6px 6px';
        return '6px';
    }

    if (isPrependIcon && isAppendIcon) return '14px 48px';
    if (isPrependIcon) return '14px 28px 14px 48px';
    if (isAppendIcon) return '14px 48px 14px 28px';
    return '14px 28px';
};

const primaryStyles = css<ButtonStyleProps>`
    background-color: ${({ theme, backgroundColor }) =>
        getBackgroundColor(backgroundColor, theme.colors.primary, theme.colors.athens_gray)};
    color: ${({ theme, backgroundColor }) =>
        getTextColor(backgroundColor, theme.colors.darker_gray, theme.colors.darker_gray)};
    border: none;
    box-shadow: ${({ backgroundColor }) => getBoxShadow(backgroundColor)};
    border-radius: ${({ compact }) => (compact ? '8px' : '24px')};

    &:hover {
        background-color: ${({ theme, backgroundColor }) =>
            getHoverBackgroundColor(
                backgroundColor,
                theme.colors.primary_90,
                theme.colors.gray_20,
            )};
    }
`;

const secondaryStyles = css<ButtonStyleProps>`
    background-color: ${({ theme, backgroundColor }) =>
        getBackgroundColor(backgroundColor, theme.colors.white, theme.colors.athens_gray)};
    color: ${({ theme, backgroundColor }) =>
        getTextColor(backgroundColor, theme.colors.primary, theme.colors.darker_gray)};
    border: ${({ backgroundColor }) => getBorder(backgroundColor)};
    border-color: ${({ theme, backgroundColor }) =>
        getBorderColor(backgroundColor, theme.colors.primary)};
    box-shadow: ${({ backgroundColor }) => getBoxShadow(backgroundColor)};
    border-radius: ${({ compact }) => (compact ? '5px' : '24px')};

    &:hover {
        background-color: ${({ theme, backgroundColor }) =>
            getHoverBackgroundColor(
                backgroundColor,
                theme.colors.primary_10,
                theme.colors.gray_20,
            )};
    }
`;

const tertiaryStyles = css<ButtonStyleProps>`
    background-color: ${({ theme }) => theme.colors.concrete};
    color: ${({ theme }) => theme.colors.darker_gray};
    border: none;
    box-shadow: none;
    text-align: left;
    border-radius: ${({ compact }) => (compact ? '5px' : '24px')};
    &:hover {
        background-color: ${({ theme }) => theme.colors.champagne};
    }
`;

const baseButtonStyles = css<ButtonStyleProps>`
    position: relative;
    padding: ${({ compact, isPrependIcon, isAppendIcon }) =>
        getPadding(compact || false, !!isPrependIcon, !!isAppendIcon)};
    min-height: ${({ compact }) => (compact ? '32px' : 'auto')};
    appearance: none;
    font-weight: ${({ theme }) => theme.font.weight.bold};
    font-size: ${({ theme, fontSize, compact }) => getFontSize(fontSize, compact || false, theme)};
    cursor: pointer;
    text-align: center;
    text-decoration: none;
    transition: ${({ theme }) => theme.transition.background};
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    border-radius: ${({ compact }) => (compact ? '5px' : '24px')};

    ${({ variant }) => {
        switch (variant) {
            case ButtonVariant.Primary:
                return primaryStyles;
            case ButtonVariant.Secondary:
                return secondaryStyles;
            case ButtonVariant.Tertiary:
                return tertiaryStyles;
            default:
                return primaryStyles;
        }
    }}

    &:disabled {
        box-shadow: none;
        pointer-events: none;
        background-color: ${({ theme, isLoading, compact }) =>
            compact ? theme.colors.gray_10 : isLoading ? '#ba9e5b' : theme.colors.gray};
        color: ${({ theme, compact }) =>
            compact ? theme.colors.gray_50 : theme.colors.light_gray};
        border-color: transparent;
    }

    &:focus-visible {
        outline: black auto 1px;
    }

    .indicator {
        position: absolute;
        left: 15%;
        transform: translate(-50%, -50%);
        top: 50%;
    }
`;

const SButton = styled.button<ButtonStyleProps>`
    ${baseButtonStyles}
`;

const SPrependIcon = styled.span<{ compact?: boolean }>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: ${({ compact }) => (compact ? '8px' : '16px')};
    display: flex;
    align-items: center;
    color: inherit;

    & svg [stroke] {
        stroke: currentColor;
    }
`;

const SAppendIcon = styled.span<{ compact?: boolean }>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: ${({ compact }) => (compact ? '8px' : '16px')};
    display: flex;
    align-items: center;
    color: inherit;

    & svg [stroke] {
        stroke: currentColor;
    }
`;

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
    children: string | ReactNode;
    onClick?: () => void;
    variant?: ButtonVariant;
    link?: boolean;
    to?: string;
    isLoading?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    compact?: boolean;
    fontSize?: ButtonFontSize;
    backgroundColor?: ButtonBackgroundColor;
    prependIcon?: ReactElement;
    appendIcon?: ReactElement;
};

export const Button = ({
    children,
    onClick,
    variant = ButtonVariant.Primary,
    link = false,
    isLoading = false,
    to,
    disabled = false,
    fullWidth = false,
    compact = false,
    fontSize,
    backgroundColor,
    prependIcon,
    appendIcon,
    ...buttonProps
}: Props) => {
    if (link && to) {
        return (
            <SButton
                as={Link}
                to={to}
                variant={variant}
                fullWidth={fullWidth}
                compact={compact}
                fontSize={fontSize}
                backgroundColor={backgroundColor}
                isPrependIcon={!!prependIcon}
                isAppendIcon={!!appendIcon}>
                {prependIcon && <SPrependIcon compact={compact}>{prependIcon}</SPrependIcon>}
                {children}
                {appendIcon && <SAppendIcon compact={compact}>{appendIcon}</SAppendIcon>}
            </SButton>
        );
    }

    return (
        <SButton
            onClick={onClick}
            variant={variant}
            disabled={isLoading || disabled}
            isLoading={isLoading}
            fullWidth={fullWidth}
            compact={compact}
            fontSize={fontSize}
            backgroundColor={backgroundColor}
            isPrependIcon={!!prependIcon}
            isAppendIcon={!!appendIcon}
            {...buttonProps}>
            {isLoading && <TailSpin className="indicator" width={25} height={25} />}
            {prependIcon && <SPrependIcon compact={compact}>{prependIcon}</SPrependIcon>}
            {children}
            {appendIcon && <SAppendIcon compact={compact}>{appendIcon}</SAppendIcon>}
        </SButton>
    );
};
